/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useParams, useRouter } from 'js/lib/useRouter';

import { Grid, LoadingSection, PageGridContainer, breakpoints } from '@coursera/cds-core';

import { NotFound } from 'bundles/common/components/withNotFound';
import GrowthDiscovery from 'bundles/epic/clients/GrowthDiscovery';
import ReviewsBackButton from 'bundles/reviews/components/ReviewsBackButton';
import ReviewsHeader from 'bundles/reviews/components/ReviewsHeader';
import ReviewsPage from 'bundles/reviews/components/ReviewsPage';
import ReviewsPageSeoElements from 'bundles/reviews/components/ReviewsPageSeoElements';
import ReviewsTable from 'bundles/reviews/components/ReviewsTable';
import ReviewsVariantCProductCard from 'bundles/reviews/components/ReviewsVariantCProductCard';
import ReviewsVariantCRatingCard from 'bundles/reviews/components/ReviewsVariantCRatingCard';
import TopReviewsSection from 'bundles/reviews/components/TopReviewsSection';
import { useReviewsPageData } from 'bundles/reviews/hooks/useReviewsPageData';
import type { ReviewRouter } from 'bundles/reviews/types/reviewTypes';
import { NotFoundError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';
import { useIsInUnifiedPageRollout } from 'bundles/unified-description-page-common/providers/UnifiedPageRolloutExperimentDataProvider';

const NUM_TOP_REVIEWS_TO_SHOW = 12;

const styles = {
  variantCLayout: css`
    display: grid;
    grid-template-columns: minmax(240px, 300px) minmax(0, 1fr);
    grid-template-areas:
      'rating product'
      'rating content';
    gap: 42px;
    align-items: start;
    margin-top: var(--cds-spacing-200);

    ${breakpoints.down('sm')} {
      grid-template-columns: 1fr;
      grid-template-areas:
        'product'
        'rating'
        'content';
      gap: var(--cds-spacing-200);
    }
  `,
  variantCLeftColumn: css`
    grid-area: rating;
    min-width: 0;
  `,
  variantCProductColumn: css`
    grid-area: product;
    min-width: 0;
  `,
  variantCContent: css`
    grid-area: content;
    min-width: 0;
  `,
};

const ReviewsPageDirector = () => {
  const router = useRouter();
  const params = useParams();
  const slug = params?.slug ?? '';
  const programSlug = params?.programSlug;
  const orgSlug = params?.orgSlug;
  const isInUnifiedPageRollout = useIsInUnifiedPageRollout();
  const variant = GrowthDiscovery.get('reviewsPageRedesignXP');

  const {
    loading,
    error,
    notFound,
    courseInfo,
    description,
    ratings,
    topReviews = [],
    schemaReviews,
    variantCHeaderData,
  } = useReviewsPageData({
    includeVariantCHeaderData: variant === 'variantC',
  });

  if (loading) {
    return <LoadingSection />;
  }

  if (!slug || notFound || error || !courseInfo?.name) {
    if (inServerContext) {
      throw new NotFoundError();
    }
    return <NotFound />;
  }

  const courseId = courseInfo.id.split('~')[1];

  let coursePageHref = `/learn/${courseInfo.slug}`;
  if (isInUnifiedPageRollout && orgSlug) {
    coursePageHref = `/organizations/${orgSlug}/learn/${courseInfo.slug}`;
  } else if (programSlug) {
    coursePageHref = `/programs/${programSlug}/learn/${courseInfo.slug}`;
  }

  const pageProps = {
    slug,
    courseInfo,
    schemaReviews: schemaReviews ?? [],
    router: router as ReviewRouter,
  };
  const backButtonProps = {
    courseName: courseInfo.name,
    href: coursePageHref,
  };
  const topReviewsSectionProps = {
    topReviews: topReviews.slice(0, NUM_TOP_REVIEWS_TO_SHOW),
  };
  const reviewsTableProps = {
    courseId: courseInfo.id,
  };
  const headerProps = {
    programSlug,
    courseName: courseInfo.name,
    courseId: courseId ?? '',
    coursePageHref,
    ratings,
    description,
  };

  if (variant === 'control') {
    return <ReviewsPage {...pageProps} />;
  }

  if (variant === 'variantB') {
    return (
      <PageGridContainer>
        <Grid item xs={12}>
          <ReviewsBackButton {...backButtonProps} />
          <ReviewsHeader {...headerProps} />
          <TopReviewsSection {...topReviewsSectionProps} />
          <ReviewsTable {...reviewsTableProps} />
          <ReviewsPageSeoElements {...pageProps} />
        </Grid>
      </PageGridContainer>
    );
  }

  if (variant === 'variantC') {
    return (
      <PageGridContainer>
        <Grid item xs={12}>
          <ReviewsBackButton {...backButtonProps} />
          <div css={styles.variantCLayout}>
            <div css={styles.variantCProductColumn}>
              <ReviewsVariantCProductCard
                {...variantCHeaderData}
                programSlug={programSlug}
                courseId={courseId ?? ''}
                courseName={variantCHeaderData?.courseName ?? courseInfo.name}
              />
            </div>
            <aside css={styles.variantCLeftColumn} data-testid="reviews-variant-c-left-column">
              <ReviewsVariantCRatingCard
                averageFiveStarRating={variantCHeaderData?.averageFiveStarRating}
                ratingCount={variantCHeaderData?.ratingCount}
                ratingCountsByStar={variantCHeaderData?.ratingCountsByStar}
              />
            </aside>
            <div css={styles.variantCContent}>
              <TopReviewsSection {...topReviewsSectionProps} />
              <ReviewsTable {...reviewsTableProps} />
            </div>
          </div>
          <ReviewsPageSeoElements {...pageProps} />
        </Grid>
      </PageGridContainer>
    );
  }

  return <ReviewsPage {...pageProps} />;
};

export default ReviewsPageDirector;
